"use strict";
/**
 * @fileoverview Functions for extracting and applying serialization metadata
 * to JSON. When a JS object is serialized to JSON it will call the `toJSON`
 * and `toString` methods on objects. When deserializing the original types of
 * those values are lost. This library exports `collectMeta` which returns a
 * JSON-serializable representation of how the original object will be
 * serialized. This can be passed to `hydrateObject` to restore the
 * original object. Currently supports `Date` and `URL` values.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.collectMeta = exports.hydrateObject = exports.EncodedType = void 0;
/** Type of value that can be restored. */
var EncodedType;
(function (EncodedType) {
    EncodedType["DATE"] = "date";
    EncodedType["URL"] = "url";
})(EncodedType = exports.EncodedType || (exports.EncodedType = {}));
/**
 * Restores the type of values after deserializing a JSON object. Meta must be
 * the object returned from `collectMeta`. Mutates the object in place.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hydrateObject(meta, value) {
    for (const [path, encoding] of Object.entries(meta)) {
        let updatingValue = value;
        const parts = path.split('.');
        for (let i = 0; i < parts.length - 1; ++i) {
            let accessor = Number(parts[i]);
            if (Number.isNaN(accessor)) {
                accessor = parts[i];
            }
            updatingValue = updatingValue[accessor];
        }
        let lastAccessor = Number(parts[parts.length - 1]);
        if (Number.isNaN(lastAccessor)) {
            lastAccessor = parts[parts.length - 1];
        }
        switch (encoding) {
            case EncodedType.DATE:
                updatingValue[lastAccessor] = new Date(updatingValue[lastAccessor]);
                break;
            case EncodedType.URL:
                updatingValue[lastAccessor] = new URL(updatingValue[lastAccessor]);
                break;
            default:
                checkExhaustive(encoding);
        }
    }
}
exports.hydrateObject = hydrateObject;
function* walk() {
    const path = [];
    do {
        const { parent, key, value } = yield path.map(([key]) => key).join('.');
        while (path.length > 0 && path[path.length - 1][1] !== parent) {
            path.pop();
        }
        path.push([key, value]);
    } while (path.length > 0);
}
function createReplacer() {
    const meta = {};
    const walker = walk();
    function replacer(key, value) {
        const path = walker.next({ parent: this, key, value }).value;
        if (path && this[key] instanceof Date) {
            meta[path] = EncodedType.DATE;
        }
        else if (path && this[key] instanceof URL) {
            meta[path] = EncodedType.URL;
        }
        return value;
    }
    return [replacer, () => meta];
}
/**
 * Walks an object and extracts information for hydrating it after
 * deserialization.
 */
function collectMeta(obj) {
    const [replacer, dumpMeta] = createReplacer();
    JSON.stringify(obj, replacer);
    return dumpMeta();
}
exports.collectMeta = collectMeta;
function checkExhaustive(cased) {
    if (process.env.NODE_ENV !== 'production') {
        throw new Error(`Unexpected condition: ${cased}`);
    }
    return undefined;
}
